// PubSubController.js
'use strict';
import throttle from 'lodash/throttle';
import { ANALYTICS_GOOGLECLOUD_ACCESS_TOKEN } from '../RemoteConfigKeys.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { pubsub } from '../resource/debug.js';

const DEFAULT_PUBSUB_TOPIC = 'projects/swag-2c052/topics/analytics';
const DEFAULT_PUBSUB_PREFIX = 'https://pubsub.googleapis.com/v1/';
const DEFAULT_PUBSUB_POSTFIX = ':publish';

const PUBSUB_LOCAL_STORAGE_KEY = '__pubsub';
const DEFAULT_THROTTLE_INTERVAL = 10 * 1000; // 10 secs

// Maximum number of messages to include in a single request to Pub/Sub API
// The API has a limit of 1000 messages per publish request
// https://cloud.google.com/pubsub/quotas#resource_limits
const MAX_CHUNK_SIZE = 1000;

const log = pubsub.extend('log');
const errorLog = pubsub.extend('error');

/**
 * Pub/Sub Controller
 */
class PubSubController {
  /**
   * @param {object} options
   * @param {string} options.pubSubTopic
   * @param {string} options.pubSubPrefix
   * @param {string} options.pubSubPostfix
   * @param {number} [options.batchMaxCount = 10]
   * @param {number} [options.interval = 10000]
   */
  constructor({
    pubSubTopic,
    pubSubPrefix,
    pubSubPostfix,
    batchMaxCount = 10,
    interval = DEFAULT_THROTTLE_INTERVAL,
  }) {
    this.queue = {
      pending: [],
      failed: [],
    };
    this.pubSubUrl = this.getPubSubUrl({
      pubSubTopic,
      pubSubPrefix,
      pubSubPostfix,
    });
    this.batchMaxCount = batchMaxCount;
    this.interval = interval;
    this.flushPendingThrottle = throttle(this.flushPending, this.interval);
    this.flushFailedThrottle = throttle(this.flushFailed, this.interval);
    this.init();
    log('create new instance', this.pubSubUrl);
  }

  async init() {
    const storedQueue = this.restore();
    if (storedQueue) {
      const queue = JSON.parse(storedQueue);
      if (queue.pending.length || queue.failed.length) {
        log('flush the stored messages');
        this.queue = queue;
        this.flushPending();
        this.flushFailed();
      }
    }
  }

  async addToQueue(payload) {
    if (payload) {
      try {
        const message = await this.generateBase64Message(payload);
        this._addToQueue(message);
        this.startFlush();
      } catch (error) {
        const { getSentry } = await import('./sentry.js');
        getSentry().then(({ withScope, captureMessage }) => {
          withScope(scope => {
            scope.setFingerprint(['pubsub', 'PubSubController.js']);
            scope.setTag('record', 'pubsub');
            captureMessage(error);
          });
        });
        errorLog(error);
      }
    }
  }

  _addToQueue(messages) {
    this.queue.pending.push(messages);
    this.save();
  }

  updatePubSubConfig({ pubSubTopic, pubSubPrefix, pubSubPostfix }) {
    this.pubSubUrl = this.getPubSubUrl({
      pubSubTopic,
      pubSubPrefix,
      pubSubPostfix,
    });
    this.startFlush();
    log('updatePubSubConfig', this.pubSubUrl);
  }

  getPubSubUrl({ pubSubTopic, pubSubPrefix, pubSubPostfix }) {
    try {
      return new URL(pubSubTopic, pubSubPrefix).href + pubSubPostfix;
    } catch (error) {
      return (
        new URL(DEFAULT_PUBSUB_TOPIC, DEFAULT_PUBSUB_PREFIX).href +
        DEFAULT_PUBSUB_POSTFIX
      );
    }
  }

  save() {
    try {
      localStorage.setItem(
        PUBSUB_LOCAL_STORAGE_KEY,
        JSON.stringify(this.queue)
      );
      // eslint-disable-next-line no-empty
    } catch (_) {}
  }

  restore() {
    let stored;
    try {
      stored = localStorage.getItem(PUBSUB_LOCAL_STORAGE_KEY);
      // eslint-disable-next-line no-empty
    } catch (_) {}

    return stored;
  }

  async startFlush() {
    if (this.queue.pending.length >= this.batchMaxCount) {
      log('flushPending immediately', this.queue.pending.length);
      this.flushPendingThrottle.cancel();
      this.flushPending();
    } else {
      log('flushPendingThrottle', this.queue.pending.length);
      this.flushPendingThrottle();
    }

    log('flushFailedThrottle', this.queue.failed.length);
    this.flushFailedThrottle();
  }

  async flushPending() {
    if (!this.queue.pending.length || !this.pubSubUrl) return;
    const messages = this.queue.pending;
    this.queue.pending = [];
    try {
      await this._flush(messages);
    } catch (failedMessages) {
      this.queue.failed.push(...failedMessages);
    } finally {
      this.save();
    }
  }

  async flushFailed() {
    if (!this.queue.failed.length || !this.pubSubUrl) return;
    const messages = this.queue.failed;
    this.queue.failed = [];
    try {
      await this._flush(messages);
    } catch (failedMessages) {
      this.queue.failed.push(...failedMessages);
    } finally {
      this.save();
    }
  }

  async _flush(messages) {
    log('flush messages to pub/sub');

    let pubSubAccessToken;

    try {
      pubSubAccessToken = await this.getPubSubAccessToken();
    } catch (error) {
      const { getSentry } = await import('./sentry.js');
      getSentry().then(({ withScope, captureMessage }) => {
        withScope(scope => {
          scope.setFingerprint([
            'pubsub',
            'PubSubController.js - getPubSubAccessToken',
          ]);
          scope.setTag('record', 'pubsub');
          captureMessage(error);
        });
      });
      errorLog(error);

      // throw messages to be handled by the caller
      throw messages;
    }

    if (pubSubAccessToken) {
      // Split messages into chunks of 1000
      const chunks = [];
      for (let i = 0; i < messages.length; i += MAX_CHUNK_SIZE) {
        chunks.push(messages.slice(i, i + MAX_CHUNK_SIZE));
      }

      const results = await Promise.allSettled(
        chunks.map(async chunk => {
          const response = await fetch(this.pubSubUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${pubSubAccessToken}`,
            },
            body: JSON.stringify({ messages: chunk }),
          });

          if (!response.ok) {
            let text = '';
            try {
              text = await response.text();
              // eslint-disable-next-line no-empty
            } catch (_) {}
            const error = new Error(
              `post to pubsub error, ${response.status} ${text}`
            );
            const { getSentry } = await import('./sentry.js');
            getSentry().then(({ withScope, captureMessage }) => {
              withScope(scope => {
                scope.setFingerprint([
                  'pubsub',
                  'PubSubController.js - flush message',
                ]);
                scope.setTag('record', 'pubsub');
                captureMessage(error);
              });
            });
            errorLog(error);

            throw error;
          }
        })
      );

      // Collect failed chunks to be handled by the caller
      const failedMessages = results
        .filter(result => result.status === 'rejected')
        .flatMap((_, index) => chunks[index]);

      if (failedMessages.length > 0) {
        log(`${failedMessages.length} chunks failed to send`);

        // throw failedMessages to be handled by the caller
        throw failedMessages;
      }
    }
  }

  async generateBase64Message(payload) {
    const { encode } = await import('js-base64');

    return { data: encode(JSON.stringify(payload)) };
  }

  async getPubSubAccessToken() {
    const response = await fetch(
      getResourceUrl({ endpoint: '/configurations/analytics.json' })
    );

    if (!response.ok) {
      let text = '';
      try {
        text = await response.text();
        // eslint-disable-next-line no-empty
      } catch (_) {}
      throw new Error(`getPubSubAccessToken error, ${response.status} ${text}`);
    }
    const data = await response.json();
    return data[ANALYTICS_GOOGLECLOUD_ACCESS_TOKEN];
  }
}

export default PubSubController;
